import { FC } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import { pluralizeWithCount } from "@common/helpers/string";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { SCHEDULE_MARK_BILLED_LABEL } from "@components/accounting/schedules/applications/scheduleBillingCopy";
import { MILESTONE_ACHIEVED_MENU_LABEL } from "@components/accounting/schedules/applications/scheduleLifecycleCopy";
import { SCHEDULE_CHANGE_POLICY_LABEL } from "@components/accounting/schedules/applications/scheduleLifecycleBulkCopy";
import { CommandsDirectory, CommandsListProps } from "./lib";
import { useCommands } from "./useCommands";

/** `2 schedules selected`, `3 milestones selected`, or both joined. */
export function formatScheduleBulkSelectionHeading(options: {
    scheduleCount: number;
    milestoneCount: number;
}): string {
    const parts = [
        ...(options.scheduleCount > 0 || options.milestoneCount === 0
            ? [pluralizeWithCount("schedule", options.scheduleCount)]
            : []),
        ...(options.milestoneCount > 0
            ? [pluralizeWithCount("milestone", options.milestoneCount)]
            : []),
    ];
    return `${parts.join(", ")} selected`;
}

export const ScheduleApplicationBulkCommandsList: FC<CommandsListProps> = ({
    options = {},
}) => {
    const {
        selectedScheduleApplicationCount = 0,
        onScheduleApplicationsOpenRollforward,
        onScheduleApplicationsOpenWaterfall,
        onScheduleApplicationsMarkBilled,
        scheduleApplicationsMarkBilledDisabledReason,
        selectedScheduleMilestoneCount = 0,
        onScheduleApplicationsMilestoneAchieved,
        scheduleApplicationsMilestoneAchievedDisabledReason,
        scheduleApplicationsLifecycleActions = [],
        scheduleApplicationsChangePolicyOptions,
        scheduleApplicationsChangePolicyDisabledReason,
    } = options;
    const { open } = useCommands();

    return (
        <Command.Group
            heading={formatScheduleBulkSelectionHeading({
                scheduleCount: selectedScheduleApplicationCount,
                milestoneCount: selectedScheduleMilestoneCount,
            })}
        >
            {onScheduleApplicationsOpenRollforward && (
                <CommandItem
                    keywords={["report", "rollforward", "month", "summary"]}
                    onSelect={onScheduleApplicationsOpenRollforward}
                >
                    Open in Rollforward report
                </CommandItem>
            )}
            {onScheduleApplicationsOpenWaterfall && (
                <CommandItem
                    keywords={["report", "waterfall", "month", "start"]}
                    onSelect={onScheduleApplicationsOpenWaterfall}
                >
                    Open in Waterfall report
                </CommandItem>
            )}
            {onScheduleApplicationsMarkBilled && (
                <CommandItem
                    keywords={["billed", "bill", "invoice", "accrued"]}
                    onSelect={onScheduleApplicationsMarkBilled}
                    disabled={Boolean(
                        scheduleApplicationsMarkBilledDisabledReason,
                    )}
                    disabledTooltip={
                        scheduleApplicationsMarkBilledDisabledReason
                    }
                >
                    {SCHEDULE_MARK_BILLED_LABEL}
                </CommandItem>
            )}
            {onScheduleApplicationsMilestoneAchieved && (
                <CommandItem
                    keywords={["milestone", "achieved", "recognize"]}
                    onSelect={onScheduleApplicationsMilestoneAchieved}
                    disabled={Boolean(
                        scheduleApplicationsMilestoneAchievedDisabledReason,
                    )}
                    disabledTooltip={
                        scheduleApplicationsMilestoneAchievedDisabledReason
                    }
                >
                    {MILESTONE_ACHIEVED_MENU_LABEL}
                </CommandItem>
            )}
            {scheduleApplicationsChangePolicyOptions && (
                <CommandItem
                    keywords={["change", "policy", "schedule", "replace"]}
                    disabled={Boolean(
                        scheduleApplicationsChangePolicyDisabledReason,
                    )}
                    disabledTooltip={
                        scheduleApplicationsChangePolicyDisabledReason
                    }
                    onSelect={() =>
                        open(
                            CommandsDirectory.SCHEDULE_APPLICATION_BULK_CHANGE_POLICY,
                            options,
                        )
                    }
                >
                    {SCHEDULE_CHANGE_POLICY_LABEL}
                </CommandItem>
            )}
            {scheduleApplicationsLifecycleActions.map((action) => (
                <CommandItem
                    key={action.id}
                    value={action.id}
                    keywords={action.keywords}
                    disabled={Boolean(action.disabledReason)}
                    disabledTooltip={action.disabledReason}
                    onSelect={action.onSelect}
                >
                    {action.label}
                </CommandItem>
            ))}
        </Command.Group>
    );
};
